/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1694649600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Thu Sep 14 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "churnzero",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to ChurnZero",
          "depth": 0,
          "id": "connect-to-churnzero"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing events",
          "depth": 1,
          "id": "syncing-events"
        }, {
          "value": "Syncing enrichments",
          "depth": 1,
          "id": "syncing-enrichments"
        }, {
          "value": "Syncing objects",
          "depth": 1,
          "id": "syncing-objects"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      img: "img",
      code: "code",
      h3: "h3",
      ul: "ul",
      li: "li",
      h4: "h4"
    }, _provideComponents(), props.components), {EventSyncWarning, CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Events"
              })
            }), _jsx(_components.td, {
              children: "Sync records as events to Churnzero. This is often in the form of a track call"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://app.churnzero.net/developers#schemas-event",
                children: "Event schema"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Enrichment"
              })
            }), _jsx(_components.td, {
              children: "Sync records to a Churnzero custom table"
            }), _jsx(_components.td, {
              children: "All"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://support.churnzero.com/hc/en-us/articles/360004339852-Batch-Imports-via-CSV",
                children: "Batch import API"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Objects"
              })
            }), _jsx(_components.td, {
              children: "Sync records to objects such as accounts and contacts in Churnzero"
            }), _jsx(_components.td, {
              children: "Upsert"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.a, {
                href: "https://app.churnzero.net/developers#schemas-account",
                children: "Account schema"
              }), " and ", _jsx(_components.a, {
                href: "https://app.churnzero.net/developers#schemas-contact",
                children: "contact schema"
              })]
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-churnzero",
        children: "Connect to ChurnZero"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "ChurnZero"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "ChurnZero"
        }), " by entering a ChurnZero ", _jsx(_components.strong, {
          children: "App Key"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["To find your app key, go to ", _jsx(_components.strong, {
          children: "Admin > Application Keys > New App Key"
        }), " in ChurnZero.\nCreate a new key with a descriptive name, for example, \"Hightouch Integration\"."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-churnzero-setup-key.png",
          alt: "ChurnZero API Key"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Though optional, it's best to enter your ", _jsx(_components.strong, {
          children: "App URL"
        }), " into Hightouch.\nYou can find it below the Application Keys in ChurnZero.\nDo not include the ", _jsx(_components.code, {
          children: "/i"
        }), " in the URL you enter into Hightouch."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-churnzero-setup-url.png",
          alt: "ChurnZero App URL"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If you don't provide your App URL, Hightouch defaults to ", _jsx(_components.code, {
          children: "https://analytics.churnzero.net/"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your ChurnZero destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the ChurnZero destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-events",
        children: "Syncing events"
      }), "\n", _jsx(_components.p, {
        children: "ChurnZero's events API requires the following event parameters:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "eventDate"
          }), ": The date/time the event occurred. ISO 8601 formatted string."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "AccountId"
          }), ": The ID of the Account associated with the event"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "ContactId"
          }), ": The ID of the Contact associated with the event"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "The sync configuration form ensures all these are set and provides some additional options."
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h4, {
        id: "event-name",
        children: "Event name"
      }), "\n", _jsxs(_components.p, {
        children: ["Providing an event name is best practice while event tracking. You can either provide a static value or select to ", _jsx(_components.strong, {
          children: "use a column"
        }), " from your model. Hightouch syncs the static or column value as the ", _jsx(_components.code, {
          children: "eventName"
        }), " parameter."]
      }), "\n", _jsx(_components.h4, {
        id: "event-timestamp",
        children: "Event timestamp"
      }), "\n", _jsxs(_components.p, {
        children: ["You can optionally select a column that contains timestamps of when events occurred. If this field is empty, Hightouch uses the time the event arrives at the server. Hightouch syncs the timestamp as the ", _jsx(_components.code, {
          children: "eventTime"
        }), " parameter the events API requires."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you select a column, it should be in ", _jsx(_components.a, {
          href: "https://www.iso.org/iso-8601-date-and-time-format.html",
          children: "ISO 8601"
        }), " format."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you sync default and custom event properties via ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "field mapping"
        }), ".\nYou must include the ", _jsx(_components.strong, {
          children: "Account External ID"
        }), " and ", _jsx(_components.strong, {
          children: "Contact External ID"
        }), " of the account and contact associated with the event."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-enrichments",
        children: "Syncing enrichments"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you sync data to custom tables through ChurnZero's ", _jsx(_components.a, {
          href: "https://support.churnzero.com/hc/en-us/articles/360004339852-Batch-Imports-via-CSV",
          children: "batch import API"
        }), ".\nTo use this API, you must first create the custom table within ChurnZero.\nYou can create custom tables by going to the admin tab in ChurnZero and selecting ", _jsx(_components.strong, {
          children: "Custom Fields"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-churnzero-custom-table.png",
          alt: "Custom Table creation in ChurnZero"
        })
      }), "\n", _jsx(_components.p, {
        children: "To locate the custom table to update, Hightouch requires these two fields in your sync configuration:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Custom table ID"
          }), ": You can find the table ID by going into ", _jsx(_components.strong, {
            children: "Admin/Imports"
          }), " and selecting the custom table name from the dropdown there."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Account ID"
          }), ": This is the user account the custom table is associated with. You can find it by clicking on your name at the top of the ChurnZero dashboard and selecting ", _jsx(_components.strong, {
            children: "My Account"
          }), ". Your ID is at the end of the URL."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["You can match rows from your model to enrichment in ChurnZero on any column in your model and any field in ChurnZero.\nEnsure the data types of the model column and ChurnZero field you select match.\nRefer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you sync custom table columns via ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "field mapping"
        }), ".\nYou can map data from any of your model columns to the columns you've already created in your custom table.\nEnsure your model columns' data types match the data types of the fields you want to sync to."]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Hightouch can only sync data to fields you've already created in your custom table in ChurnZero on the ", _jsx(_components.strong, {
            children: "Custom Fields"
          }), " page."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "syncing-objects",
        children: "Syncing objects"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing to the following ChurnZero objects:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Contacts"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Accounts"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["To match rows from your model to contacts in ChurnZero, you need to select the model column that contains values that match the ", _jsx(_components.strong, {
          children: "Contact External ID"
        }), " field.\nTo match rows from your model to contacts in ChurnZero, you need to select the model column that contains values that match the ", _jsx(_components.strong, {
          children: "Account External ID"
        }), " field.\nYou can find these identifiers in the top left of your contact or account page."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "External identifiers are not the same as the ID in the URL of a contact or account page."
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-2",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you sync account and contact fields via ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "field mapping"
        }), ".\nYou can map data from any of your model columns to default and custom fields.\nEnsure your model columns data types match the data types of the fields you want to sync to."]
      }), "\n", _jsxs(_components.p, {
        children: ["You can find the custom fields for your ChurnZero workspace in ", _jsx(_components.strong, {
          children: "Admin > Custom Fields"
        }), ".\nGo to either the contact or account table to see the custom fields for that object type.\nWhen syncing custom fields, use the ", _jsx(_components.strong, {
          children: "CZ API Name"
        }), " as the destination field name in the Hightouch mapper."]
      }), "\n", _jsxs(_components.p, {
        children: ["ChurnZero requires that you map at least one default field.\nIf you use primarily custom fields, make sure to also map at least one default field.\nVisit the ChurnZero's ", _jsx(_components.a, {
          href: "https://app.churnzero.net/developers#schemas",
          children: "API documentation"
        }), " to see objects' default fields."]
      }), "\n", _jsxs(_components.p, {
        children: ["When syncing contacts, you must map a field to the contact's ", _jsx(_components.strong, {
          children: "Acount Exernal ID"
        }), ".\nPlease make sure that the contact's associated account exists in ChurnZero before attempting to sync contact information."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h4, {
        id: "404---null",
        children: "404 - \"null\""
      }), "\n", _jsxs(_components.p, {
        children: ["You may receive a ", _jsx(_components.code, {
          children: "404 - \"null\""
        }), " error because there is a trailing ", _jsx(_components.code, {
          children: "/"
        }), " at the end of the App URL within the Destination configurations. Remove the trailing ", _jsx(_components.code, {
          children: "/"
        }), " from the App URL to clear the error."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "ChurnZero's API doesn't return detailed error messages.\nFor example, it doesn't state whether an account ID or a custom field you are attempting to sync to exists.\nTherefore, Hightouch is unable to display this type of information in the debugger."
        })
      }), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
